import type { AnalyticsConsent } from './analyticsConsent'

export interface PostHogClientLike {
  init(token: string, config: Record<string, unknown>): unknown
  capture(name: string, properties?: Record<string, unknown>): unknown
  opt_in_capturing(options?: { captureEventName?: string | null | false }): unknown
  opt_out_capturing(): unknown
  startSessionRecording(): unknown
  stopSessionRecording(): unknown
}

export interface PostHogControllerOptions {
  token: string | undefined
  config: Record<string, unknown>
  readConsent: () => AnalyticsConsent | null
  loadClient: () => Promise<PostHogClientLike>
  getCurrentUrl?: () => string
}

/**
 * Lazy, consent-aware PostHog lifecycle. Merely importing this controller does
 * not import, construct, initialize, or contact PostHog.
 */
export function createPostHogController(options: PostHogControllerOptions) {
  let availableClient: PostHogClientLike | null = null
  let loadingClient: Promise<PostHogClientLike | null> | null = null
  let initialized = false
  let optedOut = false
  let lastPageviewUrl: string | undefined

  function emitPageview(client: PostHogClientLike): void {
    const url = options.getCurrentUrl?.()
    client.capture('$pageview', url ? { $current_url: url } : undefined)
    lastPageviewUrl = url
  }

  async function loadClient(): Promise<PostHogClientLike | null> {
    if (availableClient) return availableClient
    if (loadingClient) return loadingClient

    loadingClient = options
      .loadClient()
      .then((client) => {
        availableClient = client
        return client
      })
      .catch(() => null)
      .finally(() => {
        loadingClient = null
      })

    return loadingClient
  }

  async function initializeIfConsented(): Promise<PostHogClientLike | null> {
    if (!options.token || options.readConsent() !== 'granted') return null

    const client = await loadClient()
    // The visitor may withdraw while the dynamic import is in flight. Recheck
    // immediately before the first SDK construction/initialization call.
    if (!client || options.readConsent() !== 'granted') return null

    if (!initialized) {
      client.init(options.token, options.config)
      initialized = true
      // PostHog persists its own opt-out independently of our consent value.
      // A controller recreated after a reload cannot infer that SDK state, so
      // every initial explicit grant must reconcile it after initialization.
      client.opt_in_capturing({ captureEventName: false })
      client.startSessionRecording()
      // Auto pageviews are disabled in the client config. Emit exactly one
      // only after persisted opt-out state has been reconciled.
      emitPageview(client)
      optedOut = false
    } else if (optedOut) {
      client.opt_in_capturing({ captureEventName: false })
      client.startSessionRecording()
      emitPageview(client)
      optedOut = false
    }

    return client
  }

  async function capture(name: string, properties?: Record<string, unknown>): Promise<void> {
    if (options.readConsent() !== 'granted') return
    const client = await initializeIfConsented()
    if (!client || options.readConsent() !== 'granted') return
    client.capture(name, properties)
  }

  async function capturePageview(): Promise<void> {
    const client = await initializeIfConsented()
    if (!client || options.readConsent() !== 'granted') return
    const url = options.getCurrentUrl?.()
    if (url && url !== lastPageviewUrl) emitPageview(client)
  }

  async function applyConsent(consent: AnalyticsConsent): Promise<void> {
    if (consent === 'granted') {
      await initializeIfConsented()
      return
    }

    // A denial before first opt-in must not even load the SDK.
    if (!initialized || !availableClient) return
    // Opt out before stopping the recorder: stop can flush a final buffered
    // segment, and the provider-level denial must already be active first.
    availableClient.opt_out_capturing()
    availableClient.stopSessionRecording()
    optedOut = true
  }

  return { initializeIfConsented, capture, capturePageview, applyConsent }
}
